import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef } from 'react';
import styles from './DescriptionListItem.module.scss';
import { setupTransitionInTimeline } from './DescriptionListItem.transitions';
import type { DescriptionListItemProps, DescriptionListItemRef } from './DescriptionListItem.data';
import { useScrollTransition } from 'util/react-transition-component';
import Text from '../../../atoms/text/Text';
import Heading from '../../../atoms/heading/Heading';
import type { HeadingRef } from '../../../atoms/heading/Heading.data';
import type { TextRef } from '../../../atoms/text/Text.data';

export default function DescriptionListItem({
  blok,
  ref,
  ...props
}: DescriptionListItemProps & { ref?: Ref<DescriptionListItemRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const lineRef = useRef<HTMLSpanElement>(null);
  const numberRef = useRef<HeadingRef>(null);
  const titleRef = useRef<HeadingRef>(null);
  const descriptionRef = useRef<TextRef>(null);

  useScrollTransition({
    scroll: {
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        lineRef,
        numberRef,
        titleRef,
        descriptionRef,
      },
      setupTransitionInTimeline,
    }),
  });

  return (
    <div
      ref={elementRef}
      className={classNames(styles.descriptionListItem, props.className, 'grid-gutter')}
      {...sbEditable(blok)}
    >
      <span ref={lineRef} className={classNames(styles.line)} />
      <Heading
        ref={numberRef}
        disableTriggerInOnScroll
        as="span"
        size="heading-04"
        className={classNames(styles.number)}
      >
        {`0${props.index + 1}`.slice(-2)}
      </Heading>

      {blok.title && (
        <Heading className={styles.itemTitle} as="h3" size="heading-04" ref={titleRef}>
          {blok.title}
        </Heading>
      )}

      <Text
        ref={descriptionRef}
        className={classNames(styles.description)}
        as="p"
        size="body-large"
        disableTriggerInOnScroll
      >
        {blok.description}
      </Text>
    </div>
  );
}
